import type { SkuSupplyCandidatesResponse } from '@nadoutong/contracts';
import { useQuery } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';
import { useSession } from '../hooks/useSession.tsx';
import { opsApiRequest } from './client.ts';

/**
 * Supply candidates for one SKU's binding panel (epic #1327 PR-3/PR-4).
 *
 * The gateway answers "where could this SKU be bound, and at what
 * procurement multiplier" in one read, because candidacy depends on the
 * live-capability chain, the model-granular cost row, the current default
 * pool, and the canonical egress guard all at once. Reassembling that
 * client-side would drift from what the bind gate actually enforces.
 *
 * A 404 here is `CHANNEL_MODEL_NOT_FOUND`: the SKU is absent from the
 * published `pub_sku_catalog` projection (since #1411 P4-05 the gateway's
 * only identity source — platform rewrites it on every catalog mutation,
 * so there is no deploy lag to blame). A 503 means the projection itself
 * was unreadable. Callers distinguish these from a bare 404; see
 * `SupplyPanel`.
 */
export function getSkuSupplyCandidates(
  skuId: string,
  fetchImpl: typeof fetch = fetch,
  signal?: AbortSignal,
): Promise<SkuSupplyCandidatesResponse> {
  return opsApiRequest<SkuSupplyCandidatesResponse>(
    `/v1/ops/tenant-routing/sku-supply-candidates/${encodeURIComponent(skuId)}`,
    { signal },
    fetchImpl,
  );
}

export const SKU_SUPPLY_CANDIDATES_KEY = ['sku-supply-candidates'] as const;

export function useSkuSupplyCandidates(
  skuId: string | null,
): UseQueryResult<SkuSupplyCandidatesResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: [...SKU_SUPPLY_CANDIDATES_KEY, skuId ?? ''],
    queryFn: ({ signal }) => {
      if (skuId === null || skuId.length === 0) {
        throw new Error('useSkuSupplyCandidates: skuId is required');
      }
      return getSkuSupplyCandidates(skuId, fetchImpl, signal);
    },
    enabled: skuId !== null && skuId.length > 0,
  });
}
